/** Eine Woche, wie sie die Kalenderansicht zeigt: Montag bis Sonntag. */
export interface WeekRange {
  from: Date;
  to: Date;
  days: Date[];
}

export type CalendarView = "overview" | "day" | "week" | "month";

export function startOfWeek(date: Date): Date {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  // getDay(): 0 = Sonntag. In Deutschland beginnt die Woche am Montag.
  const offset = (result.getDay() + 6) % 7;
  result.setDate(result.getDate() - offset);
  return result;
}

export function weekRange(anchor: Date): WeekRange {
  const from = startOfWeek(anchor);
  const to = new Date(from);
  to.setDate(to.getDate() + 7);

  const days: Date[] = [];
  for (let index = 0; index < 7; index += 1) {
    const day = new Date(from);
    day.setDate(day.getDate() + index);
    days.push(day);
  }
  return { from, to, days };
}

export function addWeeks(date: Date, count: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + count * 7);
  return result;
}

export function addDays(date: Date, count: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + count);
  return result;
}

export function addMonths(date: Date, count: number): Date {
  const result = new Date(date);
  result.setDate(1);
  result.setMonth(result.getMonth() + count);
  return result;
}

export function calendarRange(view: CalendarView, anchor: Date): WeekRange {
  if (view === "week") {
    return weekRange(anchor);
  }

  let from = new Date(anchor);
  from.setHours(0, 0, 0, 0);
  let length = 1;
  if (view === "overview") {
    length = 30;
  } else if (view === "month") {
    from = startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
    length = 42;
  }

  const days = Array.from({ length }, (_, index) => addDays(from, index));
  return { from, to: addDays(from, length), days };
}

export function moveCalendarAnchor(view: CalendarView, anchor: Date, amount: number): Date {
  if (view === "month") {
    return addMonths(anchor, amount);
  }
  if (view === "week") {
    return addWeeks(anchor, amount);
  }
  return addDays(anchor, amount);
}

export function calendarPeriodLabel(view: CalendarView, anchor: Date, range: WeekRange): string {
  if (view === "month") {
    return anchor.toLocaleDateString("de-DE", { month: "long", year: "numeric" });
  }
  if (view === "week" || view === "overview") {
    const last = addDays(range.to, -1);
    return `${range.from.toLocaleDateString("de-DE", { day: "2-digit", month: "short" })} – ${last.toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}`;
  }
  return anchor.toLocaleDateString("de-DE", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export function isSameDay(left: Date, right: Date): boolean {
  return (
    left.getFullYear() === right.getFullYear() &&
    left.getMonth() === right.getMonth() &&
    left.getDate() === right.getDate()
  );
}

/** Überlappt ein Termin diesen Tag? Ein Termin kann über Mitternacht laufen. */
export function occursOn(day: Date, startsAt: string, endsAt: string): boolean {
  const dayStart = new Date(day);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(dayStart);
  dayEnd.setDate(dayEnd.getDate() + 1);

  return new Date(startsAt) < dayEnd && new Date(endsAt) > dayStart;
}

export function formatTime(value: string): string {
  return new Date(value).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
}

/** Lokale Zeit im Format, das ein datetime-local-Feld erwartet. */
export function toLocalInput(date: Date): string {
  const pad = (value: number): string => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}
